<template>
  <section>
    <template v-if="state !== STATE.MERGE_INTENT">
      <div class="mb-8">
        <div class="flex mb-4">
          <TextInput
            id="edit-guest-fname"
            label="First Name"
            class="w-1/2 pr-4"
            v-model="form.first_name"
          />
          <TextInput
            id="edit-guest-lname"
            label="Last Name"
            class="w-1/2"
            v-model="form.last_name"
          />
        </div>
        <TextInput
          id="edit-guest-phone"
          label="Phone"
          class="mb-4 pr-4 w-1/2"
          v-model="form.phone"
        />
        <TextInput id="edit-guest-email" label="Email" class="mb-4" v-model="form.email" />
        <TextareaInput id="edit-guest-notes" label="Notes" class="mb-4" v-model="form.notes" />
        <ToggleTagList
          v-if="guest_tags.length > 0"
          :tagList="guest_tags"
          id="edit-toggle-tag"
          v-model="form.tags"
          label="Guest Tags"
          class="mb-6"
        />
        <div class="text-fg-secondary text-xs" id="edit-guest-notes-hint">
          Changes to this Guest info will reflect on all associated Reservations.
        </div>
      </div>
      <template v-if="state == STATE.SAVING">
        <div>Saving...</div>
        <LoaderLine />
      </template>
      <template v-else>
        <ErrorDisplay v-if="state == STATE.ERROR" v-bind="ui.error" />
        <div v-if="ui.error.code == 'duplicate_user'">
          <span
            id="merge-intent-btn"
            class="text-fg-link-primary cursor-pointer mb-4"
            @click="setState(STATE.MERGE_INTENT)"
          >
            Click here to merge Guest records.
          </span>
        </div>
        <div class="mt-8 flex justify-between">
          <div class="w-1/3">
            <ButtonDefault
              id="edit-guest-cancel-btn"
              label="Discard Edits"
              class="w-full"
              @click="$emit('cancel')"
              alt
            />
          </div>
          <div class="w-1/3">
            <ButtonDefault id="edit-guest-save-btn" class="w-full" label="Save" @click="save" />
          </div>
        </div>
      </template>
    </template>
    <template v-else>
      <template v-if="!ui.merged">
        <div class="flex space-x-20">
          <div id="merge-contact">
            <div class="text-fg-secondary mb-6">ID: {{ contact.id }}</div>
            <div>{{ contact.first_name }} {{ contact.last_name }}</div>
            <div>{{ contact.phone }}</div>
            <div>{{ contact.email }}</div>
            <ButtonDefault
              id="merge-contact-btn"
              class="mt-8 w-32"
              label="use this"
              @click="mergeGuest(otherGuest.id, contact.id)"
            />
          </div>
          <div v-if="otherGuest" id="merge-other-guest">
            <div class="text-fg-secondary mb-6">ID: {{ otherGuest.id }}</div>
            <div>{{ otherGuest.first_name }} {{ otherGuest.last_name }}</div>
            <div>{{ otherGuest.phone }}</div>
            <div>{{ otherGuest.email }}</div>
            <ButtonDefault
              id="merge-other-guest-btn"
              class="mt-8 w-32"
              label="use this"
              @click="mergeGuest(contact.id, otherGuest.id)"
            />
          </div>
        </div>
        <div class="text-fg-secondary text-xs mt-6">You’ll be able to edit after merging.</div>
        <div class="mt-28">
          <ButtonDefault label="back" @click="setState(STATE.INITIAL)" alt />
        </div>
      </template>
      <template v-else>
        <div>Merging of 2 guest records is complete.</div>
        <div class="mt-11 text-center">
          <ButtonDefault
            id="merge-complete-done-btn"
            label="Done"
            class="w-32"
            @click="mergeGuestClose"
          />
        </div>
      </template>
    </template>
  </section>
</template>
<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import LoaderLine from '@/common/components/atoms/LoaderLine.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import TextareaInput from '@/common/components/molecules/TextareaInput.vue'
import ToggleTagList from '@/common/components/molecules/ToggleTagList.vue'
import { useGuestStore } from '@/stores/guest'
import { useSettingStore } from '@/stores/setting'
import { blankError, clearError, displayHandled } from '@/utils/errors'

import { GuestRead, GuestWrite } from '@generated/types'

const STATE = {
  INITIAL: 'initial',
  ERROR: 'error',
  SAVING: 'saving',
  MERGE_INTENT: 'merge-intent',
}

export default defineComponent({
  name: 'GuestEditForm',
  components: {
    ToggleTagList,
    ButtonDefault,
    ErrorDisplay,
    LoaderLine,
    TextareaInput,
    TextInput,
  },
  emits: {
    updated(form: GuestWrite) {
      return !!form
    },
    cancel() {
      return true
    },
  },
  props: {
    contact: {
      type: Object as PropType<GuestRead>,
      required: true,
    },
  },
  data() {
    return {
      form: {} as GuestRead,
      state: STATE.INITIAL,
      STATE,
      ui: {
        error: blankError(),
        merged: false,
      },
    }
  },
  created() {
    this.form = { ...this.contact }
  },
  async mounted() {
    await useSettingStore().loading
  },
  computed: {
    ...mapStores(useGuestStore),
    ...mapState(useSettingStore, ['guest_tags']),
    otherGuest() {
      return this.ui.error.extra?.guest
    },
  },
  methods: {
    async save() {
      this.setState(STATE.SAVING)
      clearError(this.ui.error)
      try {
        if (!this.form) return
        await this.guestStore.updateGuest(this.form)
        this.setState(STATE.INITIAL)
        this.$emit('updated', this.form)
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.setState(STATE.ERROR)
      }
    },
    async mergeGuest(sourceId: number, targetId: number) {
      clearError(this.ui.error)
      try {
        if (!this.form) return
        await this.guestStore.mergeGuest(sourceId, targetId)
        this.ui.merged = true
        this.form.id = targetId
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.setState(STATE.ERROR)
      }
    },
    mergeGuestClose() {
      this.$emit('updated', this.form)
    },
    setState(state) {
      this.state = state
    },
  },
})
</script>
